// /admin/permissions — parallel tables for the two-layer permission model.
//
// Left panel  — Mongo team grants
//   Source:  root.teams  (loaded by loadAllTeams on session start)
//   Shows:   Every team in the active org with its assigned permission keys,
//            grouped by permission group. Read-only cross-reference — to
//            edit, click "Open in Teams" to navigate to /admin/teams?id=<id>.
//
// Right panel — Supabase governance roles
//   Source:  public.role_permissions  (legacy resource×action ACL table)
//   Shows:   role × resource matrix; each cell lists the allowed actions
//            (green badge) or blocked actions (red badge). Superadmin row is
//            highlighted. Table is currently read-only — write support is a
//            follow-up (§I10b).
//
// Access gate: admin.pages.read  (same as all other /admin/* pages).
// Scale-tier gate is intentionally NOT applied here — the scaffold needs to
// be visible during development so QA can verify layout. Gate will be
// re-added in the hardening ticket once the table is writable.
//
// §I10 — scaffold task

import { getSDK } from '../../functions/sdk.js'
import { readGovernanceClaims } from '../../functions/diagnoseAuth.js'
import {
  PERMISSION_GROUPS,
  PERMISSIONS_BY_GROUP,
  permLabel,
  isOwner,
  hasPerm
} from '../../../workspace-project/functions/permissions.js'

// ── Helpers ─────────────────────────────────────────────────────────

const _activeOrgId = (root) => root?.jwtClaims?.workspace_org_id || null

const _teamsInActiveOrg = (root) =>
  (root?.teams || []).filter((t) => t.orgId === _activeOrgId(root))

// Group the role_permissions rows into a matrix keyed by [role][resource].
// Returns { [role]: { [resource]: { allowed: string[], blocked: string[] } } }
const _buildMatrix = (rows) => {
  const m = {}
  for (const r of rows || []) {
    if (!m[r.role]) m[r.role] = {}
    if (!m[r.role][r.resource]) m[r.role][r.resource] = { allowed: [], blocked: [] }
    if (r.allowed) m[r.role][r.resource].allowed.push(r.action)
    else m[r.role][r.resource].blocked.push(r.action)
  }
  return m
}

// Distinct roles + resources in display order.
const _ROLE_ORDER = ['superadmin', 'admin', 'manager', 'member', 'advisor', 'investor']
const _RESOURCE_ORDER = ['People', 'Tickets', 'Chat', 'Documents', 'Announcements', 'Calendar']

// ── Mongo team grant panel ───────────────────────────────────────────

const _teamGrantRow = {
  display: 'grid',
  gridTemplateColumns: '1fr 2fr auto',
  gap: 'Z',
  align: 'start',
  padding: 'Z1 A',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',
  ':hover': { background: 'overlayStrong' },

  TeamName: {
    flow: 'y',
    gap: 'V',
    Name: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'title',
      text: (el, s) => s.name || s.slug || '—'
    },
    Meta: {
      tag: 'span',
      fontSize: 'Y1',
      color: 'caption',
      text: (el, s) => {
        const cat = s.category || 'active'
        const sys = s.isSystem ? ' · system' : ''
        return `${cat}${sys}`
      }
    }
  },

  Perms: {
    flow: 'x wrap',
    gap: 'V',
    children: (el, s) => {
      const keys = s.permissions || []
      if (!keys.length) {
        return [{
          tag: 'span',
          fontSize: 'Y1',
          color: 'caption',
          fontStyle: 'italic',
          text: 'no permissions'
        }]
      }
      return keys.map((k) => ({
        tag: 'span',
        padding: 'Y Z',
        round: 'Y',
        fontSize: 'Y1',
        fontWeight: '600',
        color: 'title',
        background: 'overlayStrong',
        title: k,
        text: permLabel(k)
      }))
    }
  },

  EditBtn: {
    tag: 'button',
    text: '→ Teams',
    title: 'Open in Teams & Permissions editor',
    padding: 'V Z1',
    round: 'Y',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    background: 'transparent',
    color: 'caption',
    cursor: 'pointer',
    fontSize: 'Y1',
    ':hover': { background: 'overlay', color: 'title' },
    onClick: (e, el, s) => {
      const root = el.call('findRouteRoot', el)
      if (root) el.router(`/admin/teams?id=${encodeURIComponent(s.id)}`, root)
    }
  }
}

const _mongoPanel = {
  flex: '1',
  minWidth: '0',
  flow: 'y',
  gap: 'Z1',

  PanelTitle: {
    tag: 'h2',
    fontSize: 'A1',
    fontWeight: '800',
    color: 'title',
    margin: '0',
    text: 'Mongo team grants'
  },
  PanelHint: {
    tag: 'p',
    fontSize: 'Y2',
    color: 'caption',
    margin: '0',
    lineHeight: '1.5',
    text: 'Permission keys assigned to each team in the active org. Effective access = team perms ∪ org-role perms ∪ owner bypass.'
  },

  Card: {
    round: 'Z2',
    background: 'overlay',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    overflow: 'hidden',
    flow: 'y',

    HeaderRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 2fr auto',
      gap: 'Z',
      padding: 'Z A',
      borderBottomWidth: '2px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      childProps: {
        fontSize: 'Y1',
        fontWeight: '700',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        color: 'caption'
      },
      H01: { text: 'Team' },
      H02: { text: 'Permissions' },
      H03: { text: '' }
    },

    ActiveSection: {
      flow: 'y',
      SectionLabel: {
        padding: 'V A',
        fontSize: 'Y1',
        fontWeight: '700',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        color: 'caption',
        background: 'overlayStrong',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'line',
        text: 'Active'
      },
      Rows: {
        flow: 'y',
        childExtends: _teamGrantRow,
        childrenAs: 'state',
        children: (el, s) =>
          _teamsInActiveOrg(s.root).filter((t) => (t.category || 'active') === 'active')
      },
      Empty: {
        tag: 'span',
        hide: (el, s) =>
          _teamsInActiveOrg(s.root).filter((t) => (t.category || 'active') === 'active').length > 0,
        padding: 'Z A',
        fontSize: 'Z2',
        color: 'caption',
        text: 'No active teams yet.'
      }
    },

    ExternalSection: {
      flow: 'y',
      SectionLabel: {
        padding: 'V A',
        fontSize: 'Y1',
        fontWeight: '700',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        color: 'caption',
        background: 'overlayStrong',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'line',
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: 'line',
        text: 'External'
      },
      Rows: {
        flow: 'y',
        childExtends: _teamGrantRow,
        childrenAs: 'state',
        children: (el, s) =>
          _teamsInActiveOrg(s.root).filter((t) => t.category === 'external')
      },
      Empty: {
        tag: 'span',
        hide: (el, s) =>
          _teamsInActiveOrg(s.root).filter((t) => t.category === 'external').length > 0,
        padding: 'Z A',
        fontSize: 'Z2',
        color: 'caption',
        text: 'No external teams.'
      }
    }
  }
}

// ── Supabase governance roles panel ─────────────────────────────────

// Cell showing allowed / blocked actions for a role×resource pair.
const _matrixCell = (role, resource) => ({
  padding: 'V Z',
  flow: 'x wrap',
  gap: 'V',
  borderRightWidth: '1px',
  borderRightStyle: 'solid',
  borderRightColor: 'line',
  children: (el, s) => {
    const matrix = s.root?._permMatrix || {}
    const cell = (matrix[role] || {})[resource] || { allowed: [], blocked: [] }
    const children = []
    for (const a of cell.allowed) {
      children.push({
        tag: 'span',
        padding: 'Y Z',
        round: 'Y',
        fontSize: 'Y1',
        fontWeight: '600',
        color: 'white',
        background: 'green',
        text: a
      })
    }
    for (const a of cell.blocked) {
      children.push({
        tag: 'span',
        padding: 'Y Z',
        round: 'Y',
        fontSize: 'Y1',
        fontWeight: '600',
        color: 'white',
        background: 'red',
        text: a
      })
    }
    if (!children.length) {
      children.push({
        tag: 'span',
        fontSize: 'Y1',
        color: 'caption',
        text: '—'
      })
    }
    return children
  }
})

// Build the matrix table from the fetched role_permissions rows.
const _supabasePanel = {
  flex: '1',
  minWidth: '0',
  flow: 'y',
  gap: 'Z1',

  PanelTitle: {
    tag: 'h2',
    fontSize: 'A1',
    fontWeight: '800',
    color: 'title',
    margin: '0',
    text: 'Supabase governance roles'
  },
  PanelHint: {
    tag: 'p',
    fontSize: 'Y2',
    color: 'caption',
    margin: '0',
    lineHeight: '1.5',
    text: 'Legacy resource × action ACL from public.role_permissions. Green = allowed, Red = denied. Read-only scaffold — write support in §I10b.'
  },

  LoadingHint: {
    tag: 'span',
    hide: (el, s) => !!s.root?._permMatrixLoaded,
    padding: 'Z A',
    fontSize: 'Z2',
    color: 'caption',
    text: 'Loading role permissions…'
  },

  Card: {
    hide: (el, s) => !s.root?._permMatrixLoaded,
    round: 'Z2',
    background: 'overlay',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    overflow: 'auto',
    flow: 'y',

    // Header row: role columns
    HeaderRow: {
      display: 'grid',
      gridTemplateColumns: `120px repeat(${_ROLE_ORDER.length}, 1fr)`,
      borderBottomWidth: '2px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      background: 'overlayStrong',

      ResourceHeader: {
        padding: 'V Z1',
        fontSize: 'Y1',
        fontWeight: '700',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        color: 'caption',
        borderRightWidth: '1px',
        borderRightStyle: 'solid',
        borderRightColor: 'line',
        text: 'Resource'
      },

      RoleHeaders: {
        childrenAs: 'state',
        childExtends: {
          padding: 'V Z1',
          fontSize: 'Y1',
          fontWeight: '700',
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          color: (el, s) => s.r === 'superadmin' ? 'orange' : 'caption',
          borderRightWidth: '1px',
          borderRightStyle: 'solid',
          borderRightColor: 'line',
          text: (el, s) => s.r
        },
        children: () => _ROLE_ORDER.map((r) => ({ r }))
      }
    },

    // One row per resource
    Rows: {
      flow: 'y',
      childrenAs: 'state',
      childExtends: {
        display: 'grid',
        gridTemplateColumns: `120px repeat(${_ROLE_ORDER.length}, 1fr)`,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'line',
        ':hover': { background: 'overlayStrong' },

        ResourceLabel: {
          padding: 'V Z1',
          fontSize: 'Z2',
          fontWeight: '600',
          color: 'title',
          borderRightWidth: '1px',
          borderRightStyle: 'solid',
          borderRightColor: 'line',
          text: (el, s) => s.resource
        },

        Cells: {
          childrenAs: 'state',
          childExtends: {
            padding: 'V Z',
            flow: 'x wrap',
            gap: 'V',
            borderRightWidth: '1px',
            borderRightStyle: 'solid',
            borderRightColor: 'line',
            children: (el, s) => {
              const matrix = s.root?._permMatrix || {}
              const cell = (matrix[s.role] || {})[s.resource] || { allowed: [], blocked: [] }
              const items = []
              for (const a of cell.allowed) {
                items.push({
                  tag: 'span',
                  padding: 'Y Z',
                  round: 'Y',
                  fontSize: 'Y1',
                  fontWeight: '600',
                  color: 'white',
                  background: 'green',
                  text: a
                })
              }
              for (const a of cell.blocked) {
                items.push({
                  tag: 'span',
                  padding: 'Y Z',
                  round: 'Y',
                  fontSize: 'Y1',
                  fontWeight: '600',
                  color: 'white',
                  background: 'red',
                  text: a
                })
              }
              if (!items.length) {
                items.push({
                  tag: 'span',
                  fontSize: 'Y1',
                  color: 'caption',
                  text: '—'
                })
              }
              return items
            }
          },
          children: (el, s) => _ROLE_ORDER.map((role) => ({ role, resource: s.resource }))
        }
      },
      children: () => _RESOURCE_ORDER.map((resource) => ({ resource }))
    }
  }
}

// ── Page ────────────────────────────────────────────────────────────

export const adminPermissions = {
  extends: 'AppPage',
  metadata: { title: 'Permissions / Admin / Symbols Workspace' },

  onInit: async (el, s) => {
    // Permission gate — same as all /admin/* pages. On fresh page load
    // (URL bar / refresh) `s.root.jwtClaims` is not yet populated by
    // `subscribeAuthSession`, so the gate would falsely redirect even
    // for owners. Fall back to `readGovernanceClaims()` which reads
    // the persisted JWT directly and is synchronously correct.
    const claims = s.root?.jwtClaims || readGovernanceClaims()
    if (!isOwner(claims) && !hasPerm(claims, 'admin.pages.read')) {
      const root = el.call('findRouteRoot', el)
      if (root) el.router('/', root, {}, { pushState: true })
      return
    }
    // Lazy-load the role_permissions table if not yet fetched this session.
    if (s.root?._permMatrixLoaded) return
    try {
      // migrated 2026-04-27 to SDK route workspaceProject.rolePermissions/list
      const result = await getSDK().execute('workspaceProject.rolePermissions', 'list')
      const data = Array.isArray(result) ? result : (result?.data || result?.rows || [])
      s.root.update({
        _permMatrix: _buildMatrix(data || []),
        _permMatrixLoaded: true,
        _permMatrixRaw: data || []
      })
    } catch (err) {
      console.warn('[admin/permissions] role_permissions fetch failed', err)
      s.root.update({ _permMatrix: {}, _permMatrixLoaded: true, _permMatrixRaw: [] })
    }
  },

  AdminSidebar: {},

  PermissionsMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: { Title: { text: 'Permissions Overview' } },
      RightGroup: {
        RefreshBtn: {
          tag: 'button',
          text: '↻ Refresh',
          padding: 'V Z1',
          round: 'Y',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          background: 'transparent',
          color: 'title',
          cursor: 'pointer',
          fontSize: 'Z2',
          ':hover': { background: 'overlay' },
          onClick: async (e, el, s) => {
            s.root.update({ _permMatrixLoaded: false, _permMatrix: {}, _permMatrixRaw: [] })
            try {
              // migrated 2026-04-27 to SDK route workspaceProject.rolePermissions/list
              const result = await getSDK().execute('workspaceProject.rolePermissions', 'list')
              const data = Array.isArray(result) ? result : (result?.data || result?.rows || [])
              s.root.update({
                _permMatrix: _buildMatrix(data || []),
                _permMatrixLoaded: true,
                _permMatrixRaw: data || []
              })
            } catch (err) {
              console.warn('[admin/permissions] refresh failed', err)
              s.root.update({ _permMatrix: {}, _permMatrixLoaded: true })
            }
          }
        }
      }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'B',

      Intro: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'caption',
        lineHeight: '1.6',
        maxWidth: 'M',
        text: 'This page shows both layers of the permission system side by side. The Mongo team grants (left) define the fine-grained permission keys effective for each team member. The Supabase governance roles (right) control legacy resource-level access in the governance database. These two systems operate independently — JWT claims synthesize from Mongo; Supabase RLS reads from role_permissions.'
      },

      Tables: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'B',
        alignItems: 'start',

        MongoPanel: _mongoPanel,
        SupabasePanel: _supabasePanel
      }
    }
  }
}
